import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  PlayArrow as TrialIcon,
  Block as SuspendedIcon,
  HourglassEmpty as PendingIcon,
  Add as AddIcon
} from '@mui/icons-material';
import platformService from '../services/platformService';
import usePlatformAuth from '../hooks/usePlatformAuth';

function StatCard({ title, value, icon, color = 'primary' }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: `${color}.main`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { isDealer, isSuperAdmin } = usePlatformAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await platformService.getStats();
        if (response.success) {
          setStats(response.data.stats);
        }
      } catch (err) {
        setError('Failed to load dashboard statistics');
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Dashboard
      </Typography>

      {isDealer() && (
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tenants?create=true')}
          sx={{ mb: 3 }}
        >
          Create New Tenant
        </Button>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md>
          <StatCard
            title="Total Tenants"
            value={stats?.tenants?.total || 0}
            icon={<BusinessIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <StatCard
            title="Active Tenants"
            value={stats?.tenants?.active || 0}
            icon={<BusinessIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <StatCard
            title="Trial Tenants"
            value={stats?.tenants?.trial || 0}
            icon={<TrialIcon />}
            color="warning"
          />
        </Grid>
        {isSuperAdmin() && (
          <Grid item xs={12} sm={6} md>
            <StatCard
              title="Pending Verification"
              value={stats?.tenants?.pendingVerification || 0}
              icon={<PendingIcon />}
              color="warning"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md>
          <StatCard
            title="Total Users"
            value={stats?.users?.total || 0}
            icon={<PeopleIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Typography color="text.secondary">
                Use the sidebar navigation to manage tenants.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
